/*eslint-disable */

import React, { useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { Button, Modal } from "react-bootstrap";

/**
 * Branded, self-contained modal for the "Release to Brightree" flows.
 *
 * Uses the same react-bootstrap <Modal> primitive as the app's generic PopupModal
 * (centered, static backdrop, standard header/body/footer) so it matches brand
 * standards. The body scrolls internally while the header + footer stay pinned, so
 * the Repush action button is always visible and there is no
 * horizontal scroll.
 *
 * Two modes:
 *  - mode="repush":     per-resident push status (✓/✗) with selectable failed rows.
 *  - mode="validation": "Brightree requirements not met" step grid.
 *
 * Rendered imperatively (see openReleaseBrightreeModal) so callers in common.js keep
 * their simple function-call API.
 */

const STATUS = { SUCCESS: "Success", FAILED: "Failed" };

const STYLES = `
.rbt-modal .modal-content { border: none; border-radius: 12px; overflow: hidden; }
.rbt-modal .modal-header { background: #f8f9fb; border-bottom: 1px solid #eef0f4; padding: 16px 20px; position: relative; justify-content: center; }
.rbt-modal .modal-title { font-size: 17px; font-weight: 700; color: #181c32; text-align: center; width: 100%; padding: 0 28px; }
.rbt-modal .modal-header .close { position: absolute; right: 16px; top: 50%; transform: translateY(-50%); margin: 0; padding: 0; }
.rbt-modal .modal-body { padding: 20px; }
.rbt-modal .modal-footer { border-top: 1px solid #eef0f4; padding: 12px 20px; }

.rbt-intro {
  margin: 0 0 16px 0; padding: 12px 14px; border-radius: 8px;
  font-size: 13px; line-height: 1.5; display: flex; gap: 10px; align-items: flex-start;
}
.rbt-intro.rbt-intro-danger { background: #fff5f8; color: #6c1c2c; border: 1px solid #ffd6e2; }
.rbt-intro .rbt-intro-icon { font-size: 16px; line-height: 1.2; }

.rbt-scroll { max-height: 52vh; overflow-y: auto; border: 1px solid #eef0f4; border-radius: 10px; }
.rbt-table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 13px; }
.rbt-table thead th {
  position: sticky; top: 0; z-index: 1; background: #f8f9fb;
  color: #5e6278; font-weight: 600; font-size: 11px; letter-spacing: .4px; text-transform: uppercase;
  padding: 10px 12px; text-align: left; white-space: nowrap; border-bottom: 1px solid #eef0f4;
}
.rbt-table thead th.rbt-center { text-align: center; }
.rbt-table tbody td { padding: 12px; border-bottom: 1px solid #f1f2f6; vertical-align: middle; color: #3f4254; }
.rbt-table tbody tr:last-child td { border-bottom: none; }
.rbt-table tbody tr:hover td { background: #fafbfd; }
.rbt-table tbody td.rbt-center { text-align: center; }

.rbt-resident { font-weight: 600; color: #181c32; white-space: nowrap; }
.rbt-note { display: block; margin-top: 3px; font-size: 11px; color: #7e8299; line-height: 1.4; font-weight: 400; }

.rbt-badge {
  display: inline-flex; align-items: center; gap: 5px; padding: 3px 9px; border-radius: 20px;
  font-size: 11px; font-weight: 600; white-space: nowrap;
}
.rbt-badge-ok { background: #e8faf1; color: #1e8a5b; }
.rbt-badge-todo { background: #fff0f4; color: #d63864; }
.rbt-badge-na { background: #f3f4f7; color: #a1a5b7; font-weight: 600; }
.rbt-badge-fail { background: #fff0f4; color: #d63864; }

.rbt-cb { width: 17px; height: 17px; cursor: pointer; accent-color: #3699ff; }
.rbt-detail { font-size: 12px; line-height: 1.45; color: #3f4254; }
.rbt-detail.rbt-detail-muted { color: #a1a5b7; }

.rbt-footer-count { margin-right: auto; font-size: 12px; color: #7e8299; }
`;

function stepLabel(step) {
  if (step === "CMN") return "CMN";
  if (step === "SOCreation") return "Sales Order";
  return step || "";
}

/** Repush mode: selectable per-resident results. */
function RepushBody({ results, selectedIds, setSelectedIds }) {
  const failedIds = useMemo(
    () =>
      results
        .filter((r) => r && String(r.status || "").toLowerCase() === "failed")
        .map((r) => Number(r.residentId))
        .filter((id) => id > 0),
    [results]
  );
  const allSelected = failedIds.length > 0 && failedIds.every((id) => selectedIds.includes(id));

  const toggleOne = (id, checked) =>
    setSelectedIds((prev) => (checked ? Array.from(new Set([...prev, id])) : prev.filter((x) => x !== id)));
  const toggleAll = (checked) => setSelectedIds(checked ? [...failedIds] : []);

  const statusBadge = (r) => {
    const status = String(r.status || "").toLowerCase();
    if (status === "failed") {
      return (
        <span className="rbt-badge rbt-badge-fail">
          ✗ Failed{r.failedStep ? ` · ${stepLabel(r.failedStep)}` : ""}
        </span>
      );
    }
    if (status === "success") {
      return <span className="rbt-badge rbt-badge-ok">✓ Success</span>;
    }
    // Not attempted / did not go through — gray dash (same meaning as validation grid "—").
    return <span className="rbt-badge rbt-badge-na">—</span>;
  };

  const detailText = (r) => {
    const status = String(r.status || "").toLowerCase();
    if (status === "failed") {
      return r.errorMessage || "The push to Brightree failed for this resident.";
    }
    if (status === "success") {
      return "Pushed to Brightree.";
    }
    return "Did not go through.";
  };

  return (
    <div className="rbt-scroll">
      <table className="rbt-table">
        <thead>
          <tr>
            <th className="rbt-center" style={{ width: 46 }}>
              <input
                type="checkbox"
                className="rbt-cb"
                aria-label="Select all failed residents"
                checked={allSelected}
                disabled={failedIds.length === 0}
                onChange={(e) => toggleAll(e.target.checked)}
              />
            </th>
            <th>Resident</th>
            <th className="rbt-center">Status</th>
            <th>Details</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r, idx) => {
            const isFailed = String(r.status || "").toLowerCase() === "failed";
            const id = Number(r.residentId);
            return (
              <tr key={`${id}-${idx}`}>
                <td className="rbt-center">
                  {isFailed ? (
                    <input
                      type="checkbox"
                      className="rbt-cb"
                      aria-label={`Select ${r.residentName}`}
                      checked={selectedIds.includes(id)}
                      onChange={(e) => toggleOne(id, e.target.checked)}
                    />
                  ) : null}
                </td>
                <td className="rbt-resident">{r.residentName}</td>
                <td className="rbt-center">{statusBadge(r)}</td>
                <td>
                  <span className={`rbt-detail${isFailed ? "" : " rbt-detail-muted"}`}>
                    {detailText(r)}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Validation mode: "Brightree requirements not met" step grid with per-resident repush selection. */
function ValidationBody({ globalMessages, columns, rows, selectable, selectedIds, setSelectedIds }) {
  // The "other" free-text issue is shown as a note under the resident name instead of
  // a wide column of repeated sentences, so rows stay compact and readable.
  const gridColumns = columns.filter((c) => c.key !== "other");
  const hasOther = columns.some((c) => c.key === "other");

  const selectableIds = useMemo(
    () => (selectable ? rows.map((r) => Number(r.residentId)).filter((id) => id > 0) : []),
    [selectable, rows]
  );
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));

  const toggleOne = (id, checked) =>
    setSelectedIds((prev) => (checked ? Array.from(new Set([...prev, id])) : prev.filter((x) => x !== id)));
  const toggleAll = (checked) => setSelectedIds(checked ? [...selectableIds] : []);

  const renderCell = (cell) => {
    if (cell && cell.state === "todo") return <span className="rbt-badge rbt-badge-todo">To do</span>;
    if (cell && cell.state === "na") return <span className="rbt-badge rbt-badge-na">—</span>;
    return <span className="rbt-badge rbt-badge-ok">✓</span>;
  };

  return (
    <>
      {globalMessages.map((m, i) => (
        <p key={i} className="rbt-intro rbt-intro-danger">
          <span className="rbt-intro-icon">⚠️</span>
          <span>{m}</span>
        </p>
      ))}
      {selectable ? (
        <p className="rbt-intro rbt-intro-danger">
          <span className="rbt-intro-icon">⚠️</span>
          <span>
            Fix each resident's items in Brightree, then select the resident(s) and click Repush to send only those to Brightree.
          </span>
        </p>
      ) : null}
      {rows.length > 0 ? (
        <div className="rbt-scroll">
          <table className="rbt-table">
            <thead>
              <tr>
                {selectable ? (
                  <th className="rbt-center" style={{ width: 46 }}>
                    <input
                      type="checkbox"
                      className="rbt-cb"
                      aria-label="Select all residents"
                      checked={allSelected}
                      disabled={selectableIds.length === 0}
                      onChange={(e) => toggleAll(e.target.checked)}
                    />
                  </th>
                ) : null}
                <th>Resident</th>
                {gridColumns.map((c) => (
                  <th key={c.key} className="rbt-center">
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const other = hasOther ? row.cells.other : null;
                const otherNote = other && other.state === "todo" ? other.text : null;
                const id = Number(row.residentId);
                const rowSelectable = selectable && id > 0;
                return (
                  <tr key={i}>
                    {selectable ? (
                      <td className="rbt-center">
                        {rowSelectable ? (
                          <input
                            type="checkbox"
                            className="rbt-cb"
                            aria-label={`Select ${row.displayName}`}
                            checked={selectedIds.includes(id)}
                            onChange={(e) => toggleOne(id, e.target.checked)}
                          />
                        ) : null}
                      </td>
                    ) : null}
                    <td>
                      <span className="rbt-resident">{row.displayName}</span>
                      {otherNote ? <span className="rbt-note">{otherNote}</span> : null}
                    </td>
                    {gridColumns.map((c) => (
                      <td
                        key={c.key}
                        className="rbt-center"
                        title={(row.cells[c.key] && row.cells[c.key].title) || ""}
                      >
                        {renderCell(row.cells[c.key])}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p style={{ color: "#7e8299", fontSize: 13 }}>No details available.</p>
      )}
    </>
  );
}

function ReleaseBrightreeModal(props) {
  const { mode, title, data, onConfirm, onClose } = props;
  const [show, setShow] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);

  const isRepush = mode === "repush";
  // Both modes now offer per-resident selection + Repush; validation mode only when the
  // caller wired a repush handler AND rows carry resident ids (data.selectable).
  const canSelect = isRepush || (data.selectable && typeof onConfirm === "function");

  const close = () => {
    setShow(false);
    if (typeof onClose === "function") onClose();
  };

  const confirm = () => {
    if (selectedIds.length === 0) return;
    setShow(false);
    if (typeof onConfirm === "function") onConfirm(selectedIds);
  };

  // Validation grids can have up to 6 step columns — give them more room; repush is narrower.
  const modalSize = isRepush ? "lg" : "xl";
  const canConfirm = selectedIds.length > 0;

  return (
    <Modal
      centered
      scrollable
      backdrop="static"
      keyboard={false}
      show={show}
      onHide={close}
      size={modalSize}
      dialogClassName="rbt-modal"
      aria-labelledby="releaseBrightreeModalTitle"
    >
      <style>{STYLES}</style>
      <Modal.Header closeButton>
        <Modal.Title id="releaseBrightreeModalTitle">{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isRepush ? (
          <RepushBody results={data.results} selectedIds={selectedIds} setSelectedIds={setSelectedIds} />
        ) : (
          <ValidationBody
            globalMessages={data.globalMessages}
            columns={data.columns}
            rows={data.rows}
            selectable={canSelect}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        {canSelect ? (
          <span className="rbt-footer-count">
            {selectedIds.length > 0
              ? `${selectedIds.length} resident${selectedIds.length > 1 ? "s" : ""} selected`
              : "Select at least one resident"}
          </span>
        ) : null}
        <Button variant="light" size="sm" onClick={close}>
          {canSelect ? "Cancel" : "Close"}
        </Button>
        {canSelect ? (
          <Button variant="primary" size="sm" disabled={!canConfirm} onClick={confirm}>
            {`Repush${selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}`}
          </Button>
        ) : null}
      </Modal.Footer>
    </Modal>
  );
}

/**
 * Imperatively mounts a ReleaseBrightreeModal so callers keep a simple function API.
 */
function openReleaseBrightreeModal(config) {
  const host = document.createElement("div");
  document.body.appendChild(host);

  const cleanup = () => {
    setTimeout(() => {
      ReactDOM.unmountComponentAtNode(host);
      if (host.parentNode) host.parentNode.removeChild(host);
    }, 300);
  };

  ReactDOM.render(
    <ReleaseBrightreeModal
      {...config}
      onClose={() => {
        if (typeof config.onClose === "function") config.onClose();
        cleanup();
      }}
      onConfirm={(value) => {
        if (typeof config.onConfirm === "function") config.onConfirm(value);
        cleanup();
      }}
    />,
    host
  );
}

export { ReleaseBrightreeModal, openReleaseBrightreeModal };
export default ReleaseBrightreeModal;
